/**
 * Barcode decoding, from two sources, in this order.
 *
 * 1. The browser's own `BarcodeDetector`. Present in Chrome on Android, which
 *    is what the field staff carry, and costs nothing to use - no download,
 *    no wasm, hardware-accelerated.
 * 2. zxing-wasm, loaded LAZILY and only when the native detector is absent -
 *    iOS Safari, most notably. Nobody pays for the download unless their
 *    browser needs it.
 *
 * THE WASM IS SERVED FROM OUR OWN ORIGIN, NOT A CDN.
 *
 * zxing-wasm defaults `locateFile` to a jsDelivr URL. That would mean an
 * agent standing at a customer's gate on a weak connection waiting on a
 * third-party CDN before they can scan a battery - and it would break
 * entirely on any network that blocks it. The `?url` import below makes Vite
 * emit the binary as one of our own assets and hands us its hashed path, so
 * it is fetched from the same App Service as everything else.
 */

import type { ReadInputBarcodeFormat } from "zxing-wasm/reader";

// Re-exported so the screens have one import for "scanning and serials".
export { normaliseSerial } from "./serial";

/** A decoded barcode, whichever engine read it. */
export interface BarcodeRead {
  value: string;
  /** e.g. "code_128", "qr_code" - useful when a read looks wrong. */
  format: string;
}

/**
 * The symbologies a battery label realistically carries, named twice because
 * the two engines name them differently.
 *
 * Two literal lists rather than a conversion function: converting "code_128"
 * to "Code128" by capitalising parts happens to work for most of these and
 * NOT for DataMatrix or PDF417, and the only way to make a converter
 * typecheck was a cast - which would have silently passed a format string
 * zxing does not recognise.
 */
const NATIVE_FORMATS = [
  "code_128",
  "code_39",
  "code_93",
  "codabar",
  "ean_13",
  "ean_8",
  "itf",
  "upc_a",
  "upc_e",
  "qr_code",
  "data_matrix",
  "pdf417",
] as const;

const ZXING_FORMATS: ReadInputBarcodeFormat[] = [
  "Code128",
  "Code39",
  "Code93",
  "Codabar",
  "EAN-13",
  "EAN-8",
  "ITF",
  "UPC-A",
  "UPC-E",
  "QRCode",
  "DataMatrix",
  "PDF417",
];

type NativeDetector = {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string; format: string }[]>;
};

interface BarcodeDetectorCtor {
  new (options?: { formats?: readonly string[] }): NativeDetector;
  getSupportedFormats?: () => Promise<string[]>;
}

function nativeCtor(): BarcodeDetectorCtor | undefined {
  return (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

let nativePromise: Promise<NativeDetector | undefined> | undefined;

async function native(): Promise<NativeDetector | undefined> {
  const Ctor = nativeCtor();
  if (!Ctor) return undefined;
  nativePromise ??= (async () => {
    try {
      // Ask which formats this device actually supports and request only
      // those: constructing with an unsupported format throws on some
      // builds, which would knock out scanning entirely rather than
      // degrading it.
      const supported = (await Ctor.getSupportedFormats?.()) ?? [];
      const formats = supported.length
        ? NATIVE_FORMATS.filter((format) => supported.includes(format))
        : [...NATIVE_FORMATS];
      if (formats.length === 0) return undefined;
      return new Ctor({ formats });
    } catch {
      return undefined;
    }
  })();
  return nativePromise;
}

type ZXingReader = typeof import("zxing-wasm/reader");
let zxingPromise: Promise<ZXingReader | undefined> | undefined;

async function zxing(): Promise<ZXingReader | undefined> {
  zxingPromise ??= (async () => {
    try {
      const [reader, wasmUrl] = await Promise.all([
        import("zxing-wasm/reader"),
        // Vite emits the binary as an asset and gives back its URL. Same
        // origin, same cache, same deploy.
        import("zxing-wasm/reader/zxing_reader.wasm?url").then((m) => m.default),
      ]);
      reader.prepareZXingModule({
        overrides: {
          locateFile: (path: string, prefix: string) =>
            path.endsWith(".wasm") ? wasmUrl : prefix + path,
        },
      });

      // ACTUALLY DECODE SOMETHING before claiming this engine works.
      //
      // Importing the module and calling prepareZXingModule both succeed
      // without touching the WebAssembly binary - instantiation is lazy, on
      // the first real decode. So this used to return a module that could
      // never decode anything, scanningAvailable() said yes, the camera
      // opened, and every frame failed silently inside decodeFrame's catch.
      // The user got "hold steady" forever instead of "type it instead".
      //
      // That is not hypothetical: our own Content-Security-Policy was
      // refusing to compile the wasm. One blank pixel is enough to force
      // instantiation and find out.
      const probe = new ImageData(new Uint8ClampedArray([255, 255, 255, 255]), 1, 1);
      await reader.readBarcodes(probe, { formats: ZXING_FORMATS, tryHarder: false });
      return reader;
    } catch {
      return undefined;
    }
  })();
  return zxingPromise;
}

/** True when SOME engine can decode on this device. */
export async function scanningAvailable(): Promise<boolean> {
  if (await native()) return true;
  return Boolean(await zxing());
}

/**
 * Decodes one frame. Returns undefined when the frame simply holds no
 * barcode, which is the overwhelmingly common case while the camera is being
 * pointed - so it must be cheap and must not throw.
 */
export async function decodeFrame(canvas: HTMLCanvasElement): Promise<BarcodeRead | undefined> {
  const detector = await native();
  if (detector) {
    try {
      const found = await detector.detect(canvas);
      const first = found[0];
      if (first?.rawValue) return { value: first.rawValue, format: first.format };
      return undefined;
    } catch {
      // Fall through to wasm rather than giving up: some devices throw on
      // particular frame sizes.
    }
  }

  const reader = await zxing();
  if (!reader) return undefined;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return undefined;
  try {
    const image = context.getImageData(0, 0, canvas.width, canvas.height);
    const results = await reader.readBarcodes(image, {
      tryHarder: false,
      formats: ZXING_FORMATS,
      maxNumberOfSymbols: 1,
    });
    const first = results[0];
    if (first?.text) return { value: first.text, format: first.format ?? "unknown" };
  } catch {
    return undefined;
  }
  return undefined;
}

